import { Search } from 'lucide-react';
import * as React from 'react';
import toast from 'react-hot-toast';
import { DraftDocxViewer } from '@/components/drafts/DraftDocxViewer';
import Loading from '@/components/shared/Loading';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { HeaderTabs } from '@/components/ui/HeaderTabs';
import { FileWordIcon } from '@/components/ui/icons';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/NewButton';
import { MAX_DRAFT_TITLE_LENGTH } from '@/drafting/constants';
import type { DraftDocument, DraftDocumentTemplate } from '@/hooks/useDraftDocuments';
import {
  createDraftDocument,
  useDraftDocumentTemplateDocx,
  useDraftDocumentTemplates,
} from '@/hooks/useDraftDocuments';
import { apiErrorMessage } from '@/lib/api-client';
import { cn } from '@/lib/utils';

type DraftSourceMode = 'blank' | 'template';

interface NewDraftDialogProps {
  matterGuid: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (draftDocument: DraftDocument) => void;
  initialTemplateGuid?: string | null;
  initialTemplate?: DraftDocumentTemplate | null;
}

const DEFAULT_BLANK_TITLE = 'Untitled draft';

const titleFromTemplate = (template: DraftDocumentTemplate) =>
  template.title.replace(/\.docx$/i, '').trim() || DEFAULT_BLANK_TITLE;

const scopeLabel = (scope: string) => {
  if (scope === 'system') return 'Crimson';
  if (scope === 'tenant') return 'Firm';
  if (scope === 'matter') return 'Matter';
  return scope;
};

export const NewDraftDialog = ({
  initialTemplate = null,
  initialTemplateGuid = null,
  matterGuid,
  open,
  onOpenChange,
  onCreated,
}: NewDraftDialogProps) => {
  const titleInputId = React.useId();
  const templateSearchInputId = React.useId();
  const initialGuid = initialTemplateGuid ?? initialTemplate?.template_guid ?? null;
  const [mode, setMode] = React.useState<DraftSourceMode>(initialGuid ? 'template' : 'blank');
  const [title, setTitle] = React.useState(DEFAULT_BLANK_TITLE);
  const [titleTouched, setTitleTouched] = React.useState(false);
  const [templateSearch, setTemplateSearch] = React.useState('');
  const [selectedTemplateGuid, setSelectedTemplateGuid] = React.useState<string | null>(initialGuid);
  const [isCreating, setIsCreating] = React.useState(false);
  const { templates, isLoading: templatesLoading } = useDraftDocumentTemplates(
    open ? matterGuid : undefined,
    { query: templateSearch, limit: 50 },
  );
  const templateOptions = React.useMemo(() => {
    if (!initialTemplate || templates.some((template) => template.template_guid === initialTemplate.template_guid)) {
      return templates;
    }
    return [initialTemplate, ...templates];
  }, [initialTemplate, templates]);
  const selectedTemplate = React.useMemo(
    () => templateOptions.find((template) => template.template_guid === selectedTemplateGuid) ?? null,
    [selectedTemplateGuid, templateOptions],
  );

  React.useEffect(() => {
    if (open) return;
    setMode(initialGuid ? 'template' : 'blank');
    setTitle(DEFAULT_BLANK_TITLE);
    setTitleTouched(false);
    setTemplateSearch('');
    setSelectedTemplateGuid(initialGuid);
    setIsCreating(false);
  }, [initialGuid, open]);

  React.useEffect(() => {
    if (!open || !initialGuid) return;
    setMode('template');
    setSelectedTemplateGuid(initialGuid);
  }, [initialGuid, open]);

  React.useEffect(() => {
    if (!selectedTemplate || titleTouched) return;
    setTitle(titleFromTemplate(selectedTemplate));
  }, [selectedTemplate, titleTouched]);

  const setSourceMode = React.useCallback((nextMode: DraftSourceMode) => {
    setMode(nextMode);
    if (!titleTouched) {
      setTitle(nextMode === 'blank' || !selectedTemplate
        ? DEFAULT_BLANK_TITLE
        : titleFromTemplate(selectedTemplate));
    }
  }, [selectedTemplate, titleTouched]);

  const chooseTemplate = React.useCallback((template: DraftDocumentTemplate) => {
    setSelectedTemplateGuid((current) => {
      const next = current === template.template_guid ? null : template.template_guid;
      if (next && !titleTouched) {
        setTitle(titleFromTemplate(template));
      }
      return next;
    });
  }, [titleTouched]);

  const createDraft = React.useCallback(async () => {
    const trimmedTitle = title.trim();
    const selectedTemplateGuid = selectedTemplate?.template_guid;
    if (!trimmedTitle) return;
    if (trimmedTitle.length > MAX_DRAFT_TITLE_LENGTH) {
      toast.error(`Draft name cannot exceed ${MAX_DRAFT_TITLE_LENGTH} characters`);
      return;
    }
    if (mode === 'template' && !selectedTemplateGuid) return;

    setIsCreating(true);
    try {
      const draftDocument = await createDraftDocument(matterGuid, mode === 'blank'
        ? {
          source: 'blank',
          title: trimmedTitle,
        }
        : {
          source: 'template',
          title: trimmedTitle,
          template_guid: selectedTemplateGuid,
        });
      onCreated(draftDocument);
      onOpenChange(false);
    } catch (error) {
      throw new Error(apiErrorMessage(error) || 'Failed to create draft');
    } finally {
      setIsCreating(false);
    }
  }, [matterGuid, mode, onCreated, onOpenChange, selectedTemplate, title]);

  const createDisabled = isCreating || !title.trim() || (mode === 'template' && !selectedTemplate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[86vh] w-[92vw] max-w-[1080px] flex-col gap-0 overflow-hidden p-0 sm:rounded-lg">
        <DialogHeader className="px-6 pb-3 pr-12 pt-5">
          <DialogTitle>New draft</DialogTitle>
          <DialogDescription>
            Start from a blank Word document or one of the managed drafting templates.
          </DialogDescription>
        </DialogHeader>

        <div className="shrink-0 px-6 pb-3">
          <div className="space-y-1">
            <Label htmlFor={titleInputId} className="text-xs font-medium text-slate-500">
              Draft name
            </Label>
            <Input
              id={titleInputId}
              value={title}
              onChange={(event) => {
                setTitleTouched(true);
                setTitle(event.target.value);
              }}
              placeholder="Name this draft"
              className="h-8 rounded-md"
              maxLength={MAX_DRAFT_TITLE_LENGTH}
            />
          </div>
        </div>

        <div className="shrink-0 px-6">
          <HeaderTabs
            ariaLabel="Draft source"
            items={[
              { value: 'blank', label: 'Blank document' },
              { value: 'template', label: 'Word template' },
            ]}
            value={mode}
            onChange={(value) => setSourceMode(value as DraftSourceMode)}
          />
        </div>

        <div className="min-h-0 flex-1 border-t border-slate-200">
          {mode === 'blank' ? (
            <BlankDocumentPreview />
          ) : (
            <div className="grid h-full min-h-0 min-w-0 bg-slate-50/60 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
              <div className="flex min-h-0 min-w-0 flex-col overflow-hidden border-r border-slate-200 bg-white">
                <div className="shrink-0 px-3 pb-2 pt-3">
                  <Label htmlFor={templateSearchInputId} className="sr-only">Search templates</Label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <Input
                      id={templateSearchInputId}
                      value={templateSearch}
                      onChange={(event) => setTemplateSearch(event.target.value)}
                      placeholder="Search templates"
                      className="h-8 rounded-md pl-8"
                    />
                  </div>
                </div>
                <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-2 pb-2">
                  {templatesLoading ? (
                    <div className="flex h-40 items-center justify-center">
                      <Loading size="sm" />
                    </div>
                  ) : templateOptions.length ? (
                    <div className="grid min-w-0 gap-0.5">
                      {templateOptions.map((template) => (
                        <TemplateChoice
                          key={template.template_guid}
                          template={template}
                          selected={selectedTemplateGuid === template.template_guid}
                          onClick={() => chooseTemplate(template)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex h-40 items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50/60 px-4 text-center text-sm text-slate-500">
                      No templates found.
                    </div>
                  )}
                </div>
              </div>

              <div className="hidden min-h-0 p-4 lg:block">
                <TemplatePreview
                  matterGuid={matterGuid}
                  template={selectedTemplate}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t border-slate-200 px-6 py-3">
          <Button
            variant="ghost"
            size="sm"
            text="Cancel"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          />
          <Button
            size="sm"
            text="Create draft"
            loadingText="Creating"
            loading={isCreating}
            disabled={createDisabled}
            onClick={() => {
              createDraft().catch((error) => {
                toast.error(error instanceof Error ? error.message : 'Failed to create draft');
              });
            }}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BlankDocumentPreview = () => (
  <div className="h-full min-h-0 bg-slate-50/60 p-5">
    <div className="flex h-full min-h-0 items-center justify-center overflow-hidden rounded-md bg-slate-50 px-6 py-8">
      <div
        aria-label="Blank document preview"
        className="h-full max-h-full max-w-full rounded-sm border border-slate-200 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.10),0_0_0_1px_rgba(148,163,184,0.16)]"
        role="img"
        style={{ aspectRatio: '210 / 297' }}
      />
    </div>
  </div>
);

const TemplateChoice = ({
  onClick,
  selected,
  template,
}: {
  onClick: () => void;
  selected: boolean;
  template: DraftDocumentTemplate;
}) => (
  <button
    type="button"
    className={cn(
      'flex min-h-[52px] w-full min-w-0 max-w-full items-start gap-2.5 overflow-hidden rounded-md px-2.5 py-2 text-left transition-colors',
      selected
        ? 'bg-slate-100'
        : 'hover:bg-slate-50',
    )}
    onClick={onClick}
  >
    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#e8f0fa] text-[#2B579A]">
      <FileWordIcon className="h-3.5 w-3.5" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate text-sm font-medium leading-5 text-slate-950">
          {template.title}
        </span>
        <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
          {scopeLabel(template.scope)}
        </span>
      </span>
      {template.description || template.when_to_use ? (
        <span className="mt-0.5 block truncate text-xs leading-4 text-slate-500">
          {template.description || template.when_to_use}
        </span>
      ) : null}
    </span>
  </button>
);

const TemplatePreview = ({
  matterGuid,
  template,
}: {
  matterGuid: string;
  template: DraftDocumentTemplate | null;
}) => {
  const {
    documentBuffer,
    isError,
    isLoading,
  } = useDraftDocumentTemplateDocx(matterGuid, template?.template_guid);

  if (!template) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center rounded-md border border-dashed border-slate-200 bg-white/70 px-8 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#e8f0fa] text-[#2B579A]">
          <FileWordIcon className="h-5 w-5" />
        </div>
        <p className="mt-3 text-sm font-medium text-slate-800">Select a template</p>
        <p className="mt-1 max-w-xs text-sm leading-5 text-slate-500">
          A read-only Word preview will appear here before you create the draft.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 overflow-hidden rounded-md border border-slate-200 bg-white">
      <DraftDocxViewer
        buffer={documentBuffer}
        documentId={`new-draft-template-${template.template_guid}`}
        emptyClassName="flex h-full min-h-[320px] items-center justify-center bg-slate-50/80 px-6 text-center text-sm text-slate-500"
        error={isError}
        fitToWidth
        showZoomControls
        frameClassName="draft-docx-editor h-full min-h-0 overflow-auto bg-slate-50/60"
        isLoading={isLoading}
        placeholderClassName="h-full min-h-0"
        portalZIndex={100000}
        title={template.title}
        versionKey={template.template_guid}
      />
    </div>
  );
};
